import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'
import { from, Observable, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { FSA, PayloadedAction } from '../../shared/types'
import { dispatchErrorAction, geoLocationToAAALocation, } from '../../shared/utils'
import { AAAStore } from '../../store/root-reducer'
import { serviceAvailabilityRequest } from '../advisories/advisories.actions'
import { ConfigService } from '../config/config.service'
import { selectActiveCallStatus } from '../dashboard/calls-statuses/call-status.selectors'
import { addPartialCallRequest } from '../dashboard/calls.actions'
import { AAACallStatus } from '../dashboard/calls.types'
import { MemberInfo } from '../member/member.types'
import { setServicingClubConfigsRequest } from '../servicing-club/servicing-club.actions'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import events from '../tagging/events'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { openMessageDialog, setIpRegionCoords, userDefaultCoordsLoaded } from '../ui/ui.actions'
import { selectHasUserDefaultCoords, selectIpRegionCoords, selectIsSmarterZoomEnabled, selectUserDefaultCoords } from '../ui/ui.selectors'
import { MessageDialogTypes } from '../ui/ui.types'
import { setVehicleStep } from '../vehicle/vehicle.actions'
import { resetAar } from './aar/aar.actions'
import { getLocationFromResults, hasHighwayName } from './geocode/geocode.utils'
import { GeoCoordinates } from './geocode/types'
import {
  ENSURE_LOCATION_SERVICES,
  LOCATION_NEXT,
  LOCATION_TYPE,
  LocationMeta,
  locationNext,
  LocationPayload,
  LOOKUP_CLUB_BY_BREAKDOWN_LOCATION,
  lookupClubByBreakdownLocation,
  notifySetBreakdownLocationFailure,
  requestExcludedAreas,
  requestIpRegion,
  resetHighwayExits,
  SET_BREAKDOWN_LOCATION,
  SET_EXCLUDED_AREAS,
  SET_HIGHWAY_EXITS,
  SET_LOCATION_CLUB,
  setBreakdownLocationRequest,
  setBreakdownLocationSuccess,
  setExcludedAreasFailure,
  setExcludedAreasSuccess,
  setHighwayExitsFailure,
  setHighwayExitsSuccess,
  setIsHighway,
  setLandMark,
  locationClubNotCovered,
  setLocationClubError,
  setLocationClubRequest,
  setLocationClubSuccess,
  setLocationServicesAvailable,
  setLocationType,
  setShowAdjustLocation,
} from './location.actions'
import { DEFAULT_LAT, DEFAULT_LNG } from "./location.constants"
import {
  selectBreakdownLocation,
  selectBreakdownLocationCoordinates,
  selectBreakdownLocationDetails,
  selectHasGPSAccess,
  selectIsBreakdownLocationValid,
  selectIsHighway
} from './location.selectors'
import { LocationService } from './location.service'
import {
  AAALocation,
  GenericCoordinates,
  GEOSnapToRoadsRequest,
  GeoLocation,
  HighwayExits,
  LocationClubResponse
} from './location.types'
import { findClosestCoordinateFromRoadsAPI, isAddressComplete } from './location.utils'
import { IpLocateService } from './service/ip-locate.service'
import { IpRegion } from './service/ip-locate.types'
import { LocationFacadeService } from './service/location-facade.service'
import { resetTowDestination } from './tow-location/tow-location.actions'

@Injectable()
export class LocationEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _geocodeService: LocationFacadeService,
    private errorReportingService: ErrorReportingService,
    private locationService: LocationService,
    private configService: ConfigService,
    private adobeEventService: AdobeEventService,
    private ipLocateService: IpLocateService
  ) { }

  ensureLocationServicesFunctioning = createEffect(() =>
    this.actions$.pipe(
      ofType(ENSURE_LOCATION_SERVICES),
      withLatestFrom(
        this.store$.pipe(select(selectUserDefaultCoords)),
      ),
      switchMap(([_, defaultCoords]) =>
        from(this._geocodeService.getGPSCoords(true, defaultCoords)).pipe(
          map((coordinates: GeoCoordinates) =>
            setLocationServicesAvailable({
              payload: { available: true, coordinates },
            }
            )),
          catchError(() => of(
            setLocationServicesAvailable({
              payload: { available: Boolean(defaultCoords), coordinates: defaultCoords || null },
            })
          )
          )
        )
      )
    )
  )

  requestIpRegionOnGpsUnavailable = createEffect(() =>
    this.actions$.pipe(
      ofType(setLocationServicesAvailable),
      filter((action) => !action.payload.available),
      map(() => requestIpRegion())
    )
  )

  resolveIpRegion = createEffect(() =>
    this.actions$.pipe(
      ofType(requestIpRegion),
      withLatestFrom(
        this.store$.pipe(select(selectIsSmarterZoomEnabled)),
        this.store$.pipe(select(selectHasUserDefaultCoords)),
        this.store$.pipe(select(selectIpRegionCoords))
      ),
      filter(
        ([_, isSmarterZoomEnabled, hasDefaultCoords, ipRegionCoords]) =>
          isSmarterZoomEnabled && !hasDefaultCoords && !ipRegionCoords
      ),
      switchMap(() =>
        this.ipLocateService.getIpRegion$().pipe(
          switchMap((region) => from(this.resolveRegionCoordinates(region))),
          map((coordinates: GeoCoordinates) =>
            setIpRegionCoords({ payload: coordinates })
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(error, [])
          )
        )
      )
    )
  )

  // DRRWEB-3399 — the `nearby` landmark lookup lives in NearbyLandmarkEffects (DRRWEB-3662, C1).

  loadHomeGeoAddress = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_BREAKDOWN_LOCATION.REQUEST),
      filter(
        (action: FSA<LocationPayload, LocationMeta>) =>
          action.meta &&
          (action.meta.locationType === LOCATION_TYPE.HOME ||
            action.meta.locationType === LOCATION_TYPE.HOME_LINK)
      ),
      switchMap((action: FSA<{ member: MemberInfo }, LocationMeta>) => {
        const member: MemberInfo = action.payload.member
        const address = `${member.basicAddress}, ${member.city}, ${member.stateProvince} ${member.postalCode}`

        // NOTE: If it is possible for the service to throw a SYNCHRONOUS error, a bug will be generated.
        // The subscriber may stop listening for messages.
        return this._geocodeService.getLocationFromAddress(address).pipe(
          switchMap(async (addresses) => {
            const aaaLocation = geoLocationToAAALocation({
              address,
              location: addresses[0],
              locationType: action.meta.locationType
            })
            return await this.resolveLocation({
              aaaLocation,
              allAddresses: addresses,
            })
          }),
          mergeMap((actions) => actions),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifySetBreakdownLocationFailure
            )
          )
        )
      })
    )
  )

  loadCurrentPositionAddress = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_BREAKDOWN_LOCATION.REQUEST),
      filter(
        (action: ReturnType<typeof setBreakdownLocationRequest>) =>
          action.meta &&
          (action.meta.locationType === LOCATION_TYPE.PIN_DROP ||
            action.meta.locationType === LOCATION_TYPE.QUERY_PARAMS ||
            action.meta.locationType === LOCATION_TYPE.GPS_LOCATION)
      ),
      withLatestFrom(
        this.store$.select(selectHasGPSAccess),
        this.store$.pipe(select(selectUserDefaultCoords)),
      ),
      switchMap(
        ([action, hasGpsAccess, defaultCoords]: [
          ReturnType<typeof setBreakdownLocationRequest>,
          boolean,
          GeoCoordinates
        ]) =>
          from(this.obtainCoordinates(action, hasGpsAccess, action.meta.locationType, defaultCoords)).pipe(
            filter((coords) => !(coords.lat == DEFAULT_LAT && coords.lng == DEFAULT_LNG)),
            switchMap((coords: GeoCoordinates) =>
              this._geocodeService.getLocationFromCoords(coords).pipe(
                mergeMap(async (results: GeoLocation[]) => {
                  const guess = getLocationFromResults(results, coords)
                  const aaaLocation = geoLocationToAAALocation({
                    address: guess.formatted_address,
                    location: guess,
                    locationType: action.meta.locationType,
                    coords
                  })

                  const resolveActions = await this.resolveLocation({
                    aaaLocation,
                    geoLocation: guess,
                    allAddresses: results,
                  })

                  return [
                    setLandMark({ payload: '' }),
                    ...resolveActions
                  ]
                }),
                mergeMap((actions) => actions)
              )
            ),
            catchError((error: Error) =>
              of(error).pipe(
                withLatestFrom(
                  this.store$.select(selectIsBreakdownLocationValid)
                ),
                switchMap(([err, isBreakdownLocationValid]) =>
                  this.errorReportingService.notifyErrorObservable(
                    err,
                    isBreakdownLocationValid
                      ? []
                      : [
                        setBreakdownLocationSuccess({ payload: null }),
                      ]
                  )
                )
              )
            )
          )
      )
    )
  )

  setBreakdownLocation = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_BREAKDOWN_LOCATION.REQUEST),
      filter((action: FSA) => action.meta.locationType === LOCATION_TYPE.ADDRESS_INPUT),
      switchMap((action) => {
        if (!action.payload.length) {
          return of({ type: SET_BREAKDOWN_LOCATION.SUCCESS })
        }
        return this._geocodeService.getLocationFromAddress(action.payload).pipe(
          mergeMap(async (addresses) => {
            const aaaLocation = geoLocationToAAALocation({
              address: action.payload,
              location: addresses[0],
              locationType: LOCATION_TYPE.ADDRESS_INPUT
            })
            const actions = await this.resolveLocation({
              geoLocation: addresses[0],
              aaaLocation,
              allAddresses: addresses,
            })
            const isFullAddress = isAddressComplete(aaaLocation) && aaaLocation.streetNumber !== ''
            if (!isFullAddress) {
              this.adobeEventService.sendEvent({
                eventName: AdobeEventTypes.SYSTEM,
                eventValue: events.location.LOCATION_INCOMPLETE_BREAKDOWN
              })
              actions.push(setShowAdjustLocation({
                payload: {
                  isAddressComplete: false,
                  address: action.payload
                }
              }))
            }
            return actions
          }),
          mergeMap((actions) => actions),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifySetBreakdownLocationFailure
            )
          )
        )
      })
    )
  )

  handleBreakdownLocationSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_BREAKDOWN_LOCATION.SUCCESS),
      filter(
        (action: ReturnType<typeof setBreakdownLocationSuccess>) =>
          action.payload !== null && isAddressComplete(action.payload)
      ),
      withLatestFrom(this.store$.select(selectActiveCallStatus)),
      filter(([_, activeCallStatus]) => activeCallStatus === null),
      mergeMap(
        ([action, _]: [
          ReturnType<typeof setBreakdownLocationSuccess>,
          AAACallStatus
        ]) =>
          action.payload.locationType === LOCATION_TYPE.HOME_LINK
            ? [
              locationNext(),
              addPartialCallRequest(),
              setVehicleStep({ payload: { step: 'vehicle' } }),
            ]
            : [
              resetAar(),
              resetTowDestination(),
            ]
      )
    )
  )

  getHighwayExits = createEffect(
    (): Observable<
      | ReturnType<typeof setHighwayExitsSuccess>
      | ReturnType<typeof setHighwayExitsFailure>
    > =>
      this.actions$.pipe(
        ofType(SET_HIGHWAY_EXITS.REQUEST),
        switchMap((action: PayloadedAction) =>
          this.locationService.getHighwayExits$(action.payload).pipe(
            withLatestFrom(this.store$.select(selectIsHighway)),
            filter(([_, isHighway]: [HighwayExits, boolean]) => isHighway),
            map(([payload]: [HighwayExits, boolean]) =>
              setHighwayExitsSuccess({ payload })
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                setHighwayExitsFailure
              )
            )
          )
        ),
        catchError((error) =>
          of(dispatchErrorAction(setHighwayExitsFailure, error))
        )
      )
  )

  handleSetLocationClubRequest = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setLocationClubRequest>>(
        SET_LOCATION_CLUB.REQUEST
      ),
      switchMap(
        (action: ReturnType<typeof setLocationClubRequest>) =>
          this.locationService.getClubByLocation$(action.payload).pipe(
            mergeMap((response: LocationClubResponse) =>
              response.servicingClub
                ? [
                  setLocationClubSuccess({
                    payload: {
                      club: response.servicingClub,
                      country: response.country,
                      zipcode: action.payload.zipcode,
                    },
                  }),
                ]
                : [setLocationClubError(), locationClubNotCovered()]
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                setLocationClubError
              )
            )
          )
      )
    )
  )

  handleLocationClubNotCovered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationClubNotCovered),
      map(() =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.NO_CLUB_COVERAGE,
          },
        })
      )
    )
  )

  triggerServicingClubConfigsRequest = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setLocationClubSuccess>>(
        SET_LOCATION_CLUB.SUCCESS
      ),
      withLatestFrom(this.store$.pipe(select(selectActiveCallStatus))),
      filter(([_, activeCallStatus]) => activeCallStatus === null),
      map(([action]) => setServicingClubConfigsRequest({ payload: action.payload }))
    )
  )

  triggerServiceAvailabilityRequest = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setLocationClubSuccess>>(
        SET_LOCATION_CLUB.SUCCESS
      ),
      withLatestFrom(
        this.store$.pipe(select(selectBreakdownLocationCoordinates)),
        this.store$.pipe(select(selectBreakdownLocationDetails)),
        this.store$.pipe(select(selectActiveCallStatus)),
        this.store$.pipe(select(selectBreakdownLocation)),
      ),
      filter(([_action, _coordinates, _details, activeCallStatus, _]) => activeCallStatus === null),
      map(([action, coordinates, details, _activeCallStatus, breakdownLocation]) => serviceAvailabilityRequest({
        payload: {
          clubCode: action.payload.club,
          breakdownLatitude: Number(coordinates.latitude),
          breakdownLongitude: Number(coordinates.longitude),
          locationCode: details.locationCode,
          zipCode: breakdownLocation.postalCode || breakdownLocation.zip,
        },
      }))
    )
  )

  handleExcludedAreasRequest = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestExcludedAreas>>(
        SET_EXCLUDED_AREAS.REQUEST
      ),
      switchMap((action: ReturnType<typeof requestExcludedAreas>) =>
        this.locationService.getExcludedAreas$(action.payload).pipe(
          map((response) =>
            setExcludedAreasSuccess({
              payload: response,
            })
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              setExcludedAreasFailure
            )
          )
        )
      )
    )
  )

  handleExcludedArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setExcludedAreasSuccess>>(setExcludedAreasSuccess),
      filter((action) => Boolean(action.payload.inExcludedArea && action.payload.excludedAreas.length > 0)),
      map((action) =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.EXCLUDED_AREA,
            disableClose: true,
            params: {
              excludedAreas: action.payload.excludedAreas
            }
          }
        })
      )
    )
  )

  handleLookupClubByBreakdownLocation = createEffect(() =>
    this.actions$.pipe(
      ofType(LOOKUP_CLUB_BY_BREAKDOWN_LOCATION),
      withLatestFrom(this.store$.select(selectBreakdownLocation)),
      map(([_, location]) =>
        setLocationClubRequest({
          payload: {
            latitude: location.latitude,
            longitude: location.longitude,
            zipcode: location.postalCode || location.zip,
          },
        })
      )
    )
  )

  handleNext = createEffect(() =>
    this.actions$.pipe(
      ofType(LOCATION_NEXT),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectBreakdownLocation))
          ),
          mergeMap(([_, location]) => {
            const nextActions = []
            nextActions.push(
              lookupClubByBreakdownLocation(),
              requestExcludedAreas({
                payload: {
                  latitude: location.latitude,
                  longitude: location.longitude,
                },
              })
            )

            return nextActions
          }),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifySetBreakdownLocationFailure
            )
          )
        )
      )
    )
  )

  private async resolveRegionCoordinates(region: IpRegion): Promise<GeoCoordinates> {
    try {
      return await this.geocodeRegion(region)
    } catch (error) {
      if (!region.postalCode) {
        throw error
      }
      return this.geocodeRegion({ ...region, postalCode: '' })
    }
  }

  private async geocodeRegion({ city, state, postalCode }: IpRegion): Promise<GeoCoordinates> {
    const stateAndPostalCode = [state, postalCode].filter(Boolean).join(' ')
    const address = [city, stateAndPostalCode].filter(Boolean).join(', ')
    const [result] = (await this._geocodeService.getLocationFromAddress(address).toPromise()) || []
    if (!result) {
      throw new Error(`No geocode result for IP region "${address}"`)
    }

    return {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    }
  }

  private async obtainCoordinates(
    action: ReturnType<typeof setBreakdownLocationRequest>,
    hasGpsAccess: boolean,
    locationType: LOCATION_TYPE,
    defaultCoords: GeoCoordinates
  ): Promise<GeoCoordinates> {
    const newCoords = locationType === LOCATION_TYPE.GPS_LOCATION ?
      await this._geocodeService.getGPSCoords(hasGpsAccess, defaultCoords) :
      null
    return action.payload &&
      action.payload.hasOwnProperty('lat') &&
      action.payload.hasOwnProperty('lng') &&
      (!newCoords || action.payload['lat'] === newCoords.lat || action.payload['lng'] === newCoords.lng)
      ? action.payload as GeoCoordinates
      : newCoords as GeoCoordinates
  }

  private async resolveLocation(
    {
      aaaLocation,
      geoLocation,
      allAddresses,
    }: {
      aaaLocation: AAALocation,
      geoLocation?: GeoLocation,
      allAddresses?: GeoLocation[],
    }
  ): Promise<Action[]> {
    const actions: Action[] = [
      setBreakdownLocationSuccess({ payload: aaaLocation }),
      userDefaultCoordsLoaded(),
    ]

    const resolveActionByHighwayRegex = (location: GenericCoordinates) => {
      actions.push(setIsHighway({ payload: true }));
    }

    const resolveActionBySnapToRoads = async () => {
      const originalCoords = {
        latitude: geoLocation.geometry.location.lat(),
        longitude: geoLocation.geometry.location.lng(),
      }
      try {
        const roadsRequest: GEOSnapToRoadsRequest = {
          tracePoints: [originalCoords]
        }
        const roadsResponse = await this.locationService.snapToRoads$(roadsRequest).toPromise()

        // Find the closest coordinate from Roads API response
        const closestResult = findClosestCoordinateFromRoadsAPI(roadsResponse, allAddresses || [])
        if (closestResult) {
          if (hasHighwayName.test(closestResult.formatted_address)) {
            resolveActionByHighwayRegex(originalCoords)
          }
        }
      } catch (error) {
        // Fallback to original logic if Roads API call fails
        console.error('Roads API call failed:', error)
      }
    }

    // Check if any of the geocode results contain highway names
    const hasHighwayInAnyResult = (): boolean => {
      if (!allAddresses || allAddresses.length === 0) {
        return false
      }

      return allAddresses.some(result => {
        // Check the formatted address for highway patterns
        return hasHighwayName.test(result.formatted_address)
      })
    }

    if (geoLocation) {
      actions.push(setLocationType({ payload: geoLocation }));

      // Run highway regex on each result from Reverse Geocoding response
      if (hasHighwayInAnyResult()) {
        // If highway is detected in any result, call Roads API
        await resolveActionBySnapToRoads()
      } else {
        // If no highway is detected, use the "dist" approach (original logic)
        actions.push(resetHighwayExits());
      }
    }

    return actions
  }
}
